import {IEventManagerWithAsync} from '@fantasmagames/core/src/events/_interfaces/IEventManager';

export interface WaitingEventsQueue {
	[waitingForEvent: string]: string;
}

export interface WaitingEventsFunction {
	[waitingForEvent: string]: Function;
}

export interface EventsListForFunction {
	waitingForEvents: string[];
	callback: Function;
}

export interface EventsFlowPairs {
	start: string;
	end: string;
}

export interface EventsFlowPairsQued {
	startOnEvent: string;
	startEvent: string;
	endEvent: string;
}

/**
 * class handles complex cases of events handling
 */
export class QueueEventsHandler {
	protected _eventsQueue: WaitingEventsQueue = {};
	protected _eventsFunctionsQueue: WaitingEventsFunction = {};
	protected _eventsListBeforeCallback: EventsListForFunction[] = [];

	protected _eventsFlowPairs: EventsFlowPairs[] = [];

	protected _eventManager: IEventManagerWithAsync;

	constructor(eventManager: IEventManagerWithAsync) {
		this._eventManager = eventManager;
	}

	public printData(): void {
		if (!__PRODUCTION__) {
			console.log('_eventsQueue:');
			for (const event in this._eventsQueue) {
				console.log(`${event}`);
			}

			console.log('_eventsFunctionsQueue:');
			for (const event in this._eventsFunctionsQueue) {
				console.log(`${event}`);
			}

			console.log('_eventsListBeforeCallback:');
			for (const event of this._eventsListBeforeCallback) {
				console.log('waitingForEvents:');
				for (const eventName of event.waitingForEvents) {
					console.log(`${eventName}`);
				}
			}

			console.log('_eventsFlowPairs:');
			for (const event of this._eventsFlowPairs) {
				console.log(`start: ${event.start} end: ${event.end}`);
			}
		}
	}

	/**
	 * callback is called when all the events were catched
	 * @param events - events list
	 * @param callback - callback function
	 * @param args - args for callback
	 */
	public addEventsListWithCallback(events: string[], callback: Function, ...args: any[]): void {
		const eventsListWithCallback: EventsListForFunction = {waitingForEvents: events, callback};
		this._eventsListBeforeCallback.push(eventsListWithCallback);
		for (const event of events) {
			this._eventManager.on(event, this.reactToListedEvent.bind(this, event, callback, eventsListWithCallback, ...args), this);
		}
	}

	/**
	 *
	 * @param pairs - Array of event pairs with start event to fire and end event to listen for.
	 * @param finalEvent - Final
	 */
	public addEventsPairFlow(pairs: EventsFlowPairs[], finalEvent: string | Function, ...args: any[]): void {
		if (!pairs || pairs.length === 0) {
			throw new Error(`Argument: pairs cannot be null or empty.`);
		}

		this._eventsFlowPairs = pairs;
		this._eventsFunctionsQueue = {};

		for (const pair of pairs) {
			this.addFunctionCallingEventToWaitingList(pair.end, () => {
				this._eventsFlowPairs.splice(0, 1);
				if (this._eventsFlowPairs.length === 0) {
					if (typeof finalEvent === 'string') {
						this._eventManager.emitDelayed(finalEvent);
					} else {
						finalEvent(args);
					}
				} else {
					this._eventManager.emitDelayed(this._eventsFlowPairs[0].start);
				}
			});
		}
		this._eventManager.emitDelayed(this._eventsFlowPairs[0].start);
	}

	/**
	 * callback is called on event and it will be removed from the list of events
	 * used for events sequencing
	 * @param waitingForEvent - event
	 * @param callback - callback function
	 * @param args - args for callback
	 */
	public addFunctionCallingEventToWaitingList(waitingForEvent: string, callback: Function, ...args: any[]): void {
		this._eventsFunctionsQueue[waitingForEvent] = callback;
		this._eventManager.on(
			waitingForEvent,
			this.reactToEventFunctionFromWaitingList.bind(this, waitingForEvent, callback, ...args),
			this,
		);
	}

	/**
	 * fires event on another event and removes reaction from the list of events
	 * @param waitingForEvent - event to listen to
	 * @param eventToFire - event to fire
	 * @param args - looks redundant to me
	 */
	public addEventToWaitingList(waitingForEvent: string, eventToFire: string, ...args: any[]): void {
		this._eventsQueue[waitingForEvent] = eventToFire;
		this._eventManager.on(waitingForEvent, this.reactToEventFromWaitingList.bind(this, waitingForEvent, eventToFire, ...args), this);
	}

	/**
	 * cleans current events list
	 */
	public clearEventsQueue(): void {
		for (const event in this._eventsQueue) {
			if (this._eventsQueue.hasOwnProperty(event)) {
				this._eventManager.off(event, this);
			}
		}

		this._eventsQueue = {};

		for (const event in this._eventsFunctionsQueue) {
			if (this._eventsFunctionsQueue.hasOwnProperty(event)) {
				this._eventManager.off(event, this);
			}
		}

		this._eventsFunctionsQueue = {};
	}

	/**
	 * reacts to an event
	 * @param id - event id
	 * @param data - looks redundant
	 */
	public handleWaitingList(id: string, data?: any): void {
		if (this._eventsQueue[id]) {
			this.reactToEventFromWaitingList(id, this._eventsQueue[id]);
		}

		if (this._eventsFunctionsQueue[id]) {
			this.reactToEventFunctionFromWaitingList(id, this._eventsFunctionsQueue[id]);
		}
	}

	protected reactToListedEvent(waitingForEvent: string, callback: Function, eventsList: EventsListForFunction, ...args: any[]): void {
		const index: number = eventsList.waitingForEvents.findIndex((item) => {
			return item === waitingForEvent;
		});

		if (index >= 0) {
			eventsList.waitingForEvents.splice(index, 1);
			this._eventManager.off(waitingForEvent, this);
			if (eventsList.waitingForEvents.length === 0) {
				const eventListIndex: number = this._eventsListBeforeCallback.findIndex((item) => {
					return item.callback === callback;
				});
				if (eventListIndex >= 0) {
					this._eventsListBeforeCallback.splice(eventListIndex, 1);
				}
				callback(...args);
			}
		}
	}

	protected reactToEventFromWaitingList(waitingForEvent: string, eventToFire: string, ...args: any[]): void {
		this._eventManager.off(waitingForEvent, this);
		this._eventManager.emit(eventToFire, ...args);
	}

	protected reactToEventFunctionFromWaitingList(waitingForEvent: string, callback: Function, ...args: any[]): void {
		this._eventManager.off(waitingForEvent, this);
		callback(...args);
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/flow/states/QueueEventsHandler.ts